import type { Messages } from '@ah/i18n';

/** English — optional/secondary locale (ADR-015). Any key missing here falls back to `el`. */
export const en: Messages = {
  nav: {
    findTherapist: 'Find a therapist',
    checkIn: 'Check-in',
    settings: 'Settings',
    signOut: 'Sign out',
    signIn: 'Sign in',
    back: '← Back',
    backHome: 'Home',
  },
  auth: {
    login: {
      title: 'Sign in',
      email: 'Email',
      password: 'Password',
      submit: 'Sign in',
      submitting: 'Signing in…',
      missingFields: 'Please enter your email and password.',
      twoFactorUnsupported: 'Two-factor authentication is not supported for this account type. Please contact support.',
      failed: 'Login failed. Please try again.',
      noAccount: 'No account?',
      registerLink: 'Register anonymously',
    },
    register: {
      title: 'Start anonymously',
      intro: "Pick a pseudonym — your therapist will never see your real name unless you choose to reveal it.",
      email: 'Email',
      pseudonym: 'Pseudonym',
      pseudonymPlaceholder: 'e.g. nyx_01',
      password: 'Password (min 10 chars, mixed case + number)',
      submit: 'Create anonymous account',
      submitting: 'Creating your account…',
      failed: 'Registration failed.',
      legalNoticePrefix: 'By signing up you agree to the',
      legalNoticeJoiner: 'and the',
      termsLink: 'Terms of Use',
      privacyLink: 'Privacy Policy',
      alreadyRegistered: 'Already registered?',
      loginLink: 'Sign in',
    },
  },
  home: {
    noTherapists: 'No approved therapists in this category yet.',
    loading: 'Loading…',
    allCategories: 'All categories',
    defaultTherapistName: 'Therapist',
    watchIntro: '▶ Watch the intro video',
    fromPricePerSession: 'From {{price}} / session',
    bookSession: 'Book session',
    startToBook: 'Get started to book',
    translationMissing: '(not yet translated to English)',
    speaks: 'Speaks:',
  },
  matching: {
    title: 'Find a therapist',
    specializationPlaceholder: 'Specialization (e.g. anxiety) — optional',
    languagePlaceholder: 'Session language — optional',
    anyLanguage: 'Any language',
    search: 'Search',
    searching: 'Searching…',
    loadError: 'Could not load therapists.',
    noResults: 'No therapists matched. Try a different specialization, or search with none.',
    defaultTherapistName: 'Therapist',
    generalPractice: 'General practice',
    yearsExperience: '{{years}} yrs experience',
    experienceNA: 'Experience n/a',
    bookSession: 'Book a session',
    noBookableSessions: 'No bookable sessions configured yet.',
    translationMissing: '(profile not yet in English — showing Greek)',
    speaks: 'Speaks:',
  },
  booking: {
    title: 'Book a session',
    titleWith: 'Book with {{name}}',
    defaultTherapistName: 'this therapist',
    needSignIn: 'You need to be signed in to book a session.',
    signIn: 'Sign in',
    noTherapistData: "We don't have this therapist's session list anymore (this page only works coming from a search result).",
    findTherapist: 'Find a therapist',
    sessionType: 'Session type',
    dateTime: 'Date & time',
    callMode: 'Call mode',
    anonymousMode: 'Anonymous — audio only, voice disguised, no camera',
    standardMode: 'Named — your real voice and, if you choose, video',
    missingFields: 'Please pick a session type and a date/time.',
    createError: 'Could not create the booking. Please try again.',
    submit: 'Confirm booking',
    submitting: 'Booking…',
    almostThere: 'Almost there',
    confirmedBody: "Your session with {{name}} is scheduled for {{when}}. We hold your spot as soon as payment is authorized — you'll only be charged once the therapist accepts. If the therapist doesn't respond within 72 hours, the hold is released automatically and you're not charged.",
    paymentError: 'Could not start payment. Please try again.',
    continueToPayment: 'Continue to payment',
    redirectingToPayment: 'Redirecting to payment…',
    backHome: 'Back home',
  },
  checkIn: {
    title: 'Check in',
    intro: "Write about what's on your mind. This isn't shared with anyone unless you choose to — it's a private, quick check before you book or message a therapist.",
    placeholder: "Type freely — there's no wrong way to say it.",
    ariaLabel: "What's on your mind",
    submit: 'Check in',
    submitting: 'Checking…',
    error: 'Could not check this right now.',
    riskDetected: 'What you wrote sounds like it might be really hard right now. Please consider reaching out to one of the lines below, or to someone you trust.',
    noRisk: "Thanks for checking in. You can continue to booking or messaging whenever you're ready.",
    disclaimer: "This app does not replace professional help. If you're in crisis, please contact:",
    lineKlimaka: 'ΚΛΙΜΑΚΑ — Suicide intervention line:',
    lineMinistry: 'Ministry of Health psychosocial support:',
    lineEmergency: 'Emergency:',
    lineKlimakaSuffix: '(24/7, free, anonymous)',
    lineMinistrySuffix: '(24/7, free)',
    back: 'Back',
  },
  settings: {
    title: 'Settings',
    appearance: 'Appearance',
    language: 'Language',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    deleteAccount: 'Delete account',
    scheduledFor: 'Your account is scheduled for deletion on {{date}}. You can cancel any time before then.',
    cancelling: 'Cancelling…',
    cancelRequest: 'Cancel deletion request',
    graceNotice: 'This starts a {{days}}-day grace period during which you can still cancel. After that, your profile, messages, and login are permanently anonymized. Bookings and invoices are kept only as required by tax law, disconnected from your identity. Enter your password to confirm.',
    password: 'Password',
    submitting: 'Submitting…',
    confirmDeletion: 'Confirm deletion',
    cancel: 'Cancel',
    deleteMyAccount: 'Delete my account',
    deletionRequestError: 'Could not submit the deletion request.',
    deletionCancelError: 'Could not cancel the deletion request.',
    back: '← Back',
  },
  call: {
    connecting: 'Connecting…',
    waitingForOther: 'Waiting for the other participant…',
    ending: 'Ending call…',
    ended: 'Call ended — {{duration}}',
    anonymousNotice: 'Anonymous call — your voice is disguised, camera is off.',
    mute: 'Mute',
    unmute: 'Unmute',
    endCall: 'End call',
    done: 'Done',
    back: 'Back',
    genericError: 'Something went wrong.',
    needSignIn: 'You need to be signed in to join this call.',
    joinError: 'Could not join the call.',
  },
  legal: {
    backHome: '← Home',
    termsLink: 'Terms of Use',
    privacyLink: 'Privacy Policy',
  },
  payments: {
    successTitle: 'Payment authorized',
    successBodyPrefix: 'Thanks — your payment for this session',
    successBodyBooking: '(booking {{id}})',
    successBodySuffix: "is authorized and held. You won't be charged unless the therapist accepts; if they don't respond within 72 hours, the hold is released automatically and you're not charged.",
    backHome: 'Back home',
    cancelledTitle: 'Payment not completed',
    cancelledBody: "Your booking is still reserved, but payment wasn't completed. You can try again from your bookings list.",
  },
  common: {
    loading: 'Loading…',
  },
};
